/**
 * 人物设定 Dialog · 双 Tab
 *
 * Tab A · 直接选用:网格里点哪张数字资产,就立刻用它的锁定图作为本剧人物设定
 * Tab B · AI 生成:挑一张数字资产作参考(锁定图同时进 img2img) → 2K/4K → 生成 3 张 → 选一张
 *
 * 共享:
 *  - 顶部展示当前人物设定 preview
 *  - 抽卡历史按倒序列出,任意候选可重新选定
 *  - 「存为数字资产」一键把当前 profile 快照成 Brain 资产
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ImageOff,
  ImagePlus,
  Loader2,
  Maximize2,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { InlineLightbox } from '@/pages/brain/Lightbox';
import type { BrainAsset, BrainAssetListResponse } from '@/pages/brain/types';
import type {
  CharacterLook,
  CharacterProfileGenerateResponse,
  CharacterProfileGroup,
  CharacterProfileSelectResponse,
  CharacterProfileUseAssetResponse,
  DirectorCharacterCard,
  DirectorEpisodePlan,
  DirectorNpcCard,
  DirectorTaskSnapshot,
} from './types';

type ProfileSubject = DirectorCharacterCard | DirectorNpcCard;

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  episodeId: string;
  character: ProfileSubject;
  /** 'character' (主角) | 'npc' · 决定 API 走 characters/ 还是 npcs/ */
  kind?: 'character' | 'npc';
  episodes?: DirectorEpisodePlan[];
}

type Tab = 'direct' | 'ai';
type Resolution = '2K' | '4K';
type Count = 1 | 2 | 3;
type ImageModel = 'gpt-image' | 'seedream';

interface AtlasRelationLite {
  target: string;
  kind?: string;
  dynamic?: string;
  turning_point?: string;
}

interface AtlasCharacterLite {
  id: string;
  name: string;
  role?: string;
  relations?: AtlasRelationLite[];
}

interface AtlasNpcLite {
  id: string;
  name: string;
  role?: string;
  relation?: string;
}

interface StoryAtlasLite {
  characters?: AtlasCharacterLite[];
  npc?: AtlasNpcLite[];
}

const RELATION_KIND_LABELS: Record<string, string> = {
  family: '家庭关系',
  friend: '朋友/盟友',
  ally: '盟友关系',
  rival: '对手关系',
  enemy: '敌对关系',
  mentor: '师徒/引导',
  romance: '情感关系',
  colleague: '同事关系',
  business: '商业关系',
  community: '社区关系',
};

function relationKindLabel(kind?: string | null): string {
  const key = (kind || '').trim();
  if (!key) return '';
  return RELATION_KIND_LABELS[key.toLowerCase()] ?? key;
}

function relationDisplayLabel(relation: AtlasRelationLite): string {
  const text = `${relation.dynamic ?? ''} ${relation.turning_point ?? ''}`;
  if ((relation.kind || '').toLowerCase() === 'family') {
    const familyLabels = [
      '丈夫',
      '妻子',
      '儿子',
      '女儿',
      '父亲',
      '母亲',
      '儿媳',
      '女婿',
      '公公',
      '婆婆',
      '岳父',
      '岳母',
      '兄弟',
      '姐妹',
      '哥哥',
      '弟弟',
      '姐姐',
      '妹妹',
      '亲家',
    ];
    const hit = familyLabels.find((label) => text.includes(label));
    if (hit) return hit;
  }
  return relationKindLabel(relation.kind);
}

export function CharacterProfileDialog({
  open,
  onOpenChange,
  episodeId,
  character,
  kind = 'character',
  episodes,
}: Props) {
  const qc = useQueryClient();
  const collection = kind === 'npc' ? 'npcs' : 'characters';
  const [tab, setTab] = useState<Tab>('direct');
  const [sourceAssetId, setSourceAssetId] = useState<string | null>(null);
  const [imageModel, setImageModel] = useState<ImageModel>('gpt-image');
  const [resolution, setResolution] = useState<Resolution>('2K');
  const [count, setCount] = useState<Count>(3);
  const [pollTaskId, setPollTaskId] = useState<string | null>(null);
  const [saveAsAssetOpen, setSaveAsAssetOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [realismEnhance, setRealismEnhance] = useState(false);

  // ── 多形象 ────────────────────────────────────
  const [selectedLookId, setSelectedLookId] = useState<string | null>(null);
  const [showNewLookForm, setShowNewLookForm] = useState(false);

  const looks = useMemo<CharacterLook[]>(() => {
    // 标准形象始终作为第一条(虚拟 __base__),映射 card 顶层字段;
    // 真实的额外形象拼在其后。绝不能因为存在 looks 就把标准形象隐藏掉,
    // 否则新建形象会让用户误以为默认标准形象被「覆盖」。
    const base: CharacterLook = {
      id: '__base__',
      label: '标准形象',
      episodeIndex: null,
      description: null,
      profileImageUrl: character.profileImageUrl ?? null,
      profileCandidateId: character.profileCandidateId ?? null,
      boundAssetId: character.boundAssetId ?? null,
      profileGroups: character.profileGroups ?? [],
      notes: character.notes ?? null,
    };
    return [base, ...(character.looks ?? [])];
  }, [character]);

  const activeLook = useMemo(
    () => looks.find((l) => l.id === selectedLookId) ?? looks[0] ?? null,
    [looks, selectedLookId],
  );

  const lookIdParam = activeLook && activeLook.id !== '__base__' ? activeLook.id : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['brain', 'assets', 'character'],
    queryFn: () =>
      api
        .get('brain/assets', { searchParams: { kind: 'character', limit: 200 } })
        .json<BrainAssetListResponse>(),
    enabled: open,
  });

  const lockedChars = useMemo<BrainAsset[]>(() => {
    const items = data?.items ?? [];
    return items.filter((a) => a.status === 'locked' && a.lockedImageUrl);
  }, [data]);

  // 「存为数字资产」默认名:按已有「角色N」推下一个序号(不依赖锁定状态,扫所有资产)
  const defaultAssetName = useMemo(() => {
    const items = data?.items ?? [];
    let maxN = 0;
    for (const it of items) {
      const m = /^角色(\d+)$/.exec(it.name ?? '');
      if (m) {
        const n = Number(m[1]);
        if (Number.isFinite(n) && n > maxN) maxN = n;
      }
    }
    return `角色${maxN + 1}`;
  }, [data]);

  const groups = useMemo<CharacterProfileGroup[]>(() => {
    return [...(activeLook?.profileGroups ?? character.profileGroups ?? [])];
  }, [activeLook?.profileGroups, character.profileGroups]);

  // ── 直接选用 ─────────────────────────────────
  const useAssetMut = useMutation({
    mutationFn: async (assetId: string) =>
      api
        .post(
          `director/episodes/${episodeId}/director/${collection}/${character.id}/profile/use-asset`,
          { json: { assetId, lookId: lookIdParam } },
        )
        .json<CharacterProfileUseAssetResponse>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已设为本剧人物设定');
    },
    onError: (e: Error) => toast.error('设置失败', e.message || '请重试'),
  });

  // ── AI 生成 ─────────────────────────────────
  const generateMut = useMutation({
    mutationFn: async () =>
      api
        .post(
          `director/episodes/${episodeId}/director/${collection}/${character.id}/profile/generate`,
          {
            json: {
              sourceAssetId: sourceAssetId ?? null,
              resolution,
              count,
              realismEnhance,
              lookId: lookIdParam,
              model: imageModel,
            },
          },
        )
        .json<CharacterProfileGenerateResponse>(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setPollTaskId(res.taskId);
      toast.success('开始生成', `${count} 张候选大约 30 秒内陆续返回`);
    },
    onError: (e: Error) => toast.error('生成失败', e.message || '请重试'),
  });

  // ── 轮询 ──────────────────────────────────────
  const taskQuery = useQuery({
    queryKey: ['director', 'task', pollTaskId],
    queryFn: async () => {
      const res = await api
        .get(`director/tasks/${pollTaskId}`)
        .json<DirectorTaskSnapshot>();
      return res;
    },
    enabled: !!pollTaskId,
    refetchInterval: (q) => {
      const s = (q.state.data as DirectorTaskSnapshot | undefined)?.status;
      if (!s) return 4000;
      return s === 'queued' || s === 'running' ? 4000 : false;
    },
  });

  // 仅在任务状态真正变化时刷新 plan,而非每次轮询都刷
  const prevTaskStatus = useRef<string | undefined>();
  useEffect(() => {
    const s = taskQuery.data?.status;
    if (s && s !== prevTaskStatus.current) {
      prevTaskStatus.current = s;
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    }
  }, [taskQuery.data?.status, qc, episodeId]);

  useEffect(() => {
    if (!pollTaskId) return;
    const running = (activeLook?.profileGroups ?? []).some(
      (g) => g.status === 'running' || g.status === 'queued',
    );
    if (!running) {
      setPollTaskId(null);
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    }
  }, [activeLook?.profileGroups, pollTaskId, qc, episodeId]);

  // ── 选定一张候选作为最终人物设定 ──────────────
  const selectMut = useMutation({
    mutationFn: async (vars: { groupId: string; candidateId: string }) =>
      api
        .post(
          `director/episodes/${episodeId}/director/${collection}/${character.id}/profile/select`,
          { json: { ...vars, lookId: lookIdParam } },
        )
        .json<CharacterProfileSelectResponse>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已设为本剧人物设定');
    },
    onError: (e: Error) => toast.error('选定失败', e.message || '请重试'),
  });

  const deselectMut = useMutation({
    mutationFn: async () => {
      const qs = lookIdParam ? `?look_id=${lookIdParam}` : '';
      return api
        .delete(
          `director/episodes/${episodeId}/director/${collection}/${character.id}/profile/select${qs}`,
        )
        .json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已取消选定');
    },
    onError: (e: Error) => toast.error('取消失败', e.message || '请重试'),
  });

  // ── 删除候选组 ────────────────────────────────
  const deleteGroupMut = useMutation({
    mutationFn: async (groupId: string) => {
      const qs = lookIdParam ? `?look_id=${lookIdParam}` : '';
      return api
        .delete(
          `director/episodes/${episodeId}/director/${collection}/${character.id}/profile/groups/${groupId}${qs}`,
        )
        .json<{ characterId: string; deletedGroupId: string }>();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已删除该组候选');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const hasRunningGroup = (activeLook?.profileGroups ?? []).some(
    (g) => g.status === 'running' || g.status === 'queued',
  );
  const canGenerate = !generateMut.isPending && !hasRunningGroup;

  // 兜底:即使没有 pollTaskId(例如刷新页面后),只要有 running 组就缓慢轮询 plan
  // 使用 15s 间隔而非 4s,避免频繁刷新导致性能问题
  useEffect(() => {
    if (!hasRunningGroup || pollTaskId) return;
    const t = setInterval(() => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    }, 15000);
    return () => clearInterval(t);
  }, [hasRunningGroup, pollTaskId, qc, episodeId]);

  return (
    <>
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o && lightboxUrl) return;
        onOpenChange(o);
      }}
      title={`「${character.name}」· 设置人物设定`}
      description="数字资产角色 = 形象基底。点「直接选用」立即定型;点「AI 生成」让模型基于资产 + 基础角色信息 + 当前形象说明出 3 张候选再挑。"
      className="max-w-5xl"
      preventCloseOnOutside={!!lightboxUrl}
      footer={
        <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          关闭
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex h-60 items-center justify-center text-sm text-neutral-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 加载中…
        </div>
      ) : (
        <div className="space-y-4 pb-3">
          {/* 剧情人物详情 · 编剧 Agent 产出的本剧角色信息 */}
          <CharacterDetails character={character} episodeId={episodeId} collection={collection} />

          {/* ── 形象选择器 ──────────────────────────── */}
          {looks.length > 0 && (
            <LookSelector
              looks={looks}
              activeLookId={activeLook?.id ?? null}
              characterName={character.name}
              episodes={episodes}
              episodeId={episodeId}
              characterId={character.id}
              collection={collection}
              showNewForm={showNewLookForm}
              onSelect={(id) => { setSelectedLookId(id); setShowNewLookForm(false); }}
              onToggleNew={() => setShowNewLookForm((v) => !v)}
              onCreated={(id) => { setSelectedLookId(id); setShowNewLookForm(false); }}
              onView={(url) => setLightboxUrl(url)}
            />
          )}

          {/* ── 形象补充说明 ──────────────────────────── */}
          <LookNotesField
            episodeId={episodeId}
            characterId={character.id}
            collection={collection}
            activeLook={activeLook}
            lookIdParam={lookIdParam}
            characterNotes={character.notes ?? ''}
          />

          {/* Tab 切换 */}
          <div className="flex items-center gap-1 border-b border-outline-variant/30">
            <TabBtn
              active={tab === 'direct'}
              onClick={() => setTab('direct')}
              icon={<Check className="h-3.5 w-3.5" />}
              label="直接选用"
              hint="点哪张资产即用哪张锁定图"
            />
            <TabBtn
              active={tab === 'ai'}
              onClick={() => setTab('ai')}
              icon={<Wand2 className="h-3.5 w-3.5" />}
              label="AI 生成"
              hint="可选资产基底 · 出 N 张候选再挑"
            />
          </div>

          {/* 网格 · 共享 */}
          <section>
            <header className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-neutral-800">
                数字资产角色
                <span className="ml-2 text-[11px] font-normal text-neutral-500">
                  {tab === 'direct' ? '点击即设为人物设定' : '可选一张作为生成基底(留空=基础角色信息生成)'}
                </span>
              </h3>
            </header>

            {lockedChars.length === 0 ? (
              <EmptyHint
                icon={<Sparkles className="h-5 w-5 text-outline" />}
                msg="还没有已锁定的角色卡 · 请到「数字资产 → 角色」创建并锁定后回来选用"
              />
            ) : (
              <div className="grid max-h-[400px] grid-cols-3 gap-3 overflow-y-auto pr-1">
                {lockedChars.map((a) => (
                  <AssetCard
                    key={a.assetId}
                    asset={a}
                    mode={tab}
                    selectedForAi={tab === 'ai' && sourceAssetId === a.assetId}
                    boundAsAsset={
                      tab === 'direct' &&
                      activeLook?.boundAssetId === a.assetId &&
                      !activeLook?.profileCandidateId
                    }
                    busy={useAssetMut.isPending}
                    onPick={() => {
                      if (tab === 'direct') {
                        useAssetMut.mutate(a.assetId);
                      } else {
                        setSourceAssetId(a.assetId);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </section>

          {/* AI Tab 专属:生成控制 + 历史 */}
          {tab === 'ai' && (
            <>
              <section className="rounded-lg border border-outline-variant/30 bg-surface-variant/20 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-neutral-800">
                    出图设置
                  </h3>
                  <span className="text-[11px] text-neutral-500">
                    数字资产可选 · 不选则只用基础角色信息和当前形象说明生成
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="inline-flex items-center gap-1 rounded-md border border-outline-variant/40 bg-surface p-0.5">
                    {([
                      ['gpt-image', 'GPT'] as const,
                      ['seedream', 'Seedream'] as const,
                    ]).map(([m, label]) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setImageModel(m)}
                        className={
                          'rounded px-3 py-1 text-xs transition ' +
                          (imageModel === m
                            ? 'bg-brand-500 text-white shadow-sm'
                            : 'text-neutral-600 hover:text-brand-600')
                        }
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="inline-flex items-center gap-1 rounded-md border border-outline-variant/40 bg-surface p-0.5">
                    {(['2K', '4K'] as Resolution[]).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setResolution(r)}
                        className={
                          'rounded px-3 py-1 text-xs transition ' +
                          (resolution === r
                            ? 'bg-brand-500 text-white shadow-sm'
                            : 'text-neutral-600 hover:text-brand-600')
                        }
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                  <div className="inline-flex items-center gap-1 rounded-md border border-outline-variant/40 bg-surface p-0.5">
                    {([1, 2, 3] as Count[]).map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setCount(n)}
                        className={
                          'rounded px-3 py-1 text-xs transition ' +
                          (count === n
                            ? 'bg-brand-500 text-white shadow-sm'
                            : 'text-neutral-600 hover:text-brand-600')
                        }
                      >
                        {n} 张
                      </button>
                    ))}
                  </div>
                  <label className="inline-flex cursor-pointer select-none items-center gap-1.5 text-xs text-neutral-600">
                    <input
                      type="checkbox"
                      checked={realismEnhance}
                      onChange={(e) => setRealismEnhance(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-neutral-300 text-brand-500 focus:ring-brand-400"
                    />
                    真人感
                    <span className="text-[10px] text-neutral-400">(皮肤毛孔/碎发/自然瑕疵)</span>
                  </label>
                  <Button
                    variant="agent"
                    agentColor="director"
                    size="sm"
                    disabled={!canGenerate}
                    onClick={() => generateMut.mutate()}
                  >
                    {generateMut.isPending || hasRunningGroup ? (
                      <>
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />{' '}
                        生成中…
                      </>
                    ) : (
                      <>
                        <Wand2 className="mr-1 h-3.5 w-3.5" /> 生成 {count} 张候选
                      </>
                    )}
                  </Button>
                  {!sourceAssetId && (
                    <span className="text-[11px] text-neutral-500">
                      未选数字资产 · 将仅依据基础角色信息和当前形象说明生成
                    </span>
                  )}
                </div>
              </section>

              <section>
                <header className="mb-2 flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold text-neutral-800">
                    候选历史
                    <span className="ml-2 text-[11px] font-normal text-neutral-500">
                      点任意一张作为本剧人物设定
                    </span>
                  </h3>
                </header>
                {groups.length === 0 ? (
                  <EmptyHint
                    icon={<Wand2 className="h-5 w-5 text-outline" />}
                    msg="还没有生成过,选完资产后点上面「生成 3 张候选」开始"
                  />
                ) : (
                  <div className="space-y-3">
                    {groups.map((g, i) => (
                      <GroupRow
                        key={g.id}
                        group={g}
                        index={groups.length - i}
                        selectedCandidateId={
                          activeLook?.profileCandidateId ?? null
                        }
                        onSelect={(candidateId) =>
                          selectMut.mutate({ groupId: g.id, candidateId })
                        }
                        submitting={selectMut.isPending}
                        onView={(url) => setLightboxUrl(url)}
                        onDelete={(groupId) => {
                          if (window.confirm('确定删除该组候选?此操作不可撤销。')) {
                            deleteGroupMut.mutate(groupId);
                          }
                        }}
                        deleting={
                          deleteGroupMut.isPending &&
                          deleteGroupMut.variables === g.id
                        }
                        onDeselect={() => deselectMut.mutate()}
                        deselecting={deselectMut.isPending}
                      />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}
      {lightboxUrl && (
        <InlineLightbox
          url={lightboxUrl}
          alt={`${character.name} 形象`}
          onClose={() => setLightboxUrl(null)}
        />
      )}
    </Modal>

    <SaveAsAssetConfirmDialog
      open={saveAsAssetOpen}
      onClose={() => setSaveAsAssetOpen(false)}
      character={character}
      defaultName={defaultAssetName}
      onSaved={() => {
        qc.invalidateQueries({ queryKey: ['brain', 'assets', 'character'] });
        setSaveAsAssetOpen(false);
        toast.success('已存为数字资产', '可在其他剧目复用');
      }}
    />
    </>
  );
}

// ────────────────────────────────────────────────────────────
// 子组件
// ────────────────────────────────────────────────────────────

function CharacterDetails({
  character,
  episodeId,
  collection,
}: {
  character: ProfileSubject;
  episodeId: string;
  collection: 'characters' | 'npcs';
}) {
  const qc = useQueryClient();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState('');

  const c = character as DirectorCharacterCard;
  const atlasQuery = useQuery({
    queryKey: ['atlas', episodeId],
    queryFn: () => api.get(`writer/atlas/${episodeId}`).json<StoryAtlasLite>(),
    enabled: !!episodeId,
    staleTime: 60_000,
  });
  const atlasCharacter = useMemo(() => {
    const items =
      collection === 'npcs'
        ? (atlasQuery.data?.npc ?? [])
        : (atlasQuery.data?.characters ?? []);
    const name = character.name.trim();
    const id = character.id.toLowerCase();
    return items.find((item) => item.name?.trim() === name || item.id?.toLowerCase() === id);
  }, [atlasQuery.data?.characters, atlasQuery.data?.npc, character.id, character.name, collection]);
  const relations = useMemo(() => {
    if (collection === 'npcs') {
      const relation = (atlasCharacter as AtlasNpcLite | undefined)?.relation?.trim();
      return relation ? [{ target: '人物关系', dynamic: relation }] : [];
    }
    return ((atlasCharacter as AtlasCharacterLite | undefined)?.relations ?? []).filter(
      (r) => r.target || r.kind || r.dynamic || r.turning_point,
    );
  }, [atlasCharacter, collection]);
  const fields: { key: string; label: string; value?: string | null }[] = [
    { key: 'role', label: '角色定位', value: c.role },
    { key: 'appearance', label: '外貌', value: c.appearance },
    { key: 'wardrobe', label: '服饰', value: c.wardrobe },
    { key: 'personality', label: '性格', value: c.personality },
  ];

  const patchMut = useMutation({
    mutationFn: async (payload: Record<string, string>) => {
      return api
        .patch(`director/episodes/${episodeId}/director/${collection}/${character.id}`, {
          json: payload,
        })
        .json<any>();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已保存');
      setEditingField(null);
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  const startEdit = (key: string, currentValue: string | null | undefined) => {
    setEditingField(key);
    setDraftValue(currentValue ?? '');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setDraftValue('');
  };

  const saveField = (key: string) => {
    const currentValue = c[key as keyof DirectorCharacterCard] ?? '';
    if ((draftValue.trim() || null) === (currentValue || null)) {
      setEditingField(null);
      return;
    }
    patchMut.mutate({ [key]: draftValue.trim() || '' });
  };

  const isLong = (key: string) => key === 'appearance' || key === 'personality';

  return (
    <section className="rounded-lg border border-outline-variant/30 bg-surface-variant/20 p-3">
      <header className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-800">
          剧情人物 ·「{character.name}」
        </h3>
        <span className="text-[11px] text-neutral-400">由编剧 Agent 产出</span>
      </header>
      <dl className="space-y-1.5">
        {fields.map((f) => (
          <div key={f.key} className="flex items-start gap-2 text-[11px] leading-relaxed">
            <dt className="w-14 shrink-0 pt-1 text-neutral-500">{f.label}:</dt>
            {editingField === f.key ? (
              <>
                {isLong(f.key) ? (
                  <textarea
                    autoFocus
                    className="min-h-[60px] flex-1 rounded border border-brand-400 bg-white px-2 py-1 text-neutral-700 outline-none resize-y"
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelEdit();
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveField(f.key);
                    }}
                    rows={3}
                  />
                ) : (
                  <input
                    autoFocus
                    className="flex-1 rounded border border-brand-400 bg-white px-2 py-1 text-neutral-700 outline-none"
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelEdit();
                      if (e.key === 'Enter') saveField(f.key);
                    }}
                  />
                )}
                <div className="flex shrink-0 gap-1 pt-1">
                  <button
                    className="rounded bg-brand-500 px-1.5 py-0.5 text-white hover:bg-brand-600"
                    onClick={() => saveField(f.key)}
                  >
                    <Save className="h-3 w-3" />
                  </button>
                  <button
                    className="rounded bg-neutral-200 px-1.5 py-0.5 text-neutral-600 hover:bg-neutral-300"
                    onClick={cancelEdit}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </>
            ) : (
              <dd
                className="group flex-1 cursor-pointer rounded py-1 pr-1 text-neutral-700 hover:bg-brand-50/50"
                onClick={() => startEdit(f.key, f.value)}
                title="点击编辑"
              >
                <span className={f.value ? '' : 'italic text-neutral-400'}>
                  {f.value || '点击添加…'}
                </span>
                <Pencil className="ml-1 inline h-3 w-3 text-neutral-300 opacity-0 group-hover:opacity-100" />
              </dd>
            )}
          </div>
        ))}
      </dl>
      {relations.length > 0 && (
        <div className="mt-2 border-t border-outline-variant/30 pt-2">
          <div className="flex items-start gap-2 text-[11px] leading-relaxed">
            <div className="w-14 shrink-0 pt-1 text-neutral-500">人物关系:</div>
            <div className="min-w-0 flex-1 space-y-1">
              {relations.map((r, i) => (
                <div
                  key={`${r.target || 'relation'}-${i}`}
                  className="rounded-md bg-white/70 px-2 py-1 text-neutral-700"
                >
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="font-medium text-brand-700">{r.target || '关系'}</span>
                    {relationDisplayLabel(r) && (
                      <span className="text-neutral-400">· {relationDisplayLabel(r)}</span>
                    )}
                  </div>
                  {r.dynamic && <p className="mt-0.5 text-neutral-600">{r.dynamic}</p>}
                  {r.turning_point && (
                    <p className="mt-0.5 text-neutral-400">转折：{r.turning_point}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition ' +
        (active
          ? 'border-brand-500 text-brand-700'
          : 'border-transparent text-neutral-500 hover:text-brand-600')
      }
    >
      {icon}
      <span className="font-semibold">{label}</span>
      <span className="text-[11px] font-normal text-neutral-400">· {hint}</span>
    </button>
  );
}

function AssetCard({
  asset,
  mode,
  selectedForAi,
  boundAsAsset,
  busy,
  onPick,
}: {
  asset: BrainAsset;
  mode: Tab;
  selectedForAi: boolean;
  boundAsAsset: boolean;
  busy: boolean;
  onPick: () => void;
}) {
  const highlight = mode === 'ai' ? selectedForAi : boundAsAsset;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={busy}
      className={
        'group relative overflow-hidden rounded-md border bg-surface text-left shadow-sm transition disabled:opacity-60 ' +
        (highlight
          ? 'border-brand-500 ring-2 ring-brand-200'
          : 'border-outline-variant/40 hover:border-brand-300')
      }
      title={mode === 'direct' ? '点击立即设为人物设定' : '选作生成参考'}
    >
      <div className="aspect-[2/1] bg-neutral-100">
        {asset.lockedImageUrl && (
          <img
            src={asset.lockedImageUrl}
            alt={asset.name}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      {highlight && (
        <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-white shadow">
          <Check className="h-2.5 w-2.5" />
        </span>
      )}
      <div className="truncate px-1.5 py-1 text-[10px] text-neutral-600">
        {asset.name}
      </div>
    </button>
  );
}

function GroupRow({
  group,
  index,
  selectedCandidateId,
  onSelect,
  submitting,
  onView,
  onDelete,
  deleting,
  onDeselect,
  deselecting,
}: {
  group: CharacterProfileGroup;
  index: number;
  selectedCandidateId: string | null;
  onSelect: (candidateId: string) => void;
  submitting: boolean;
  onView: (url: string) => void;
  onDelete: (groupId: string) => void;
  deleting: boolean;
  onDeselect: () => void;
  deselecting: boolean;
}) {
  const running = group.status === 'running' || group.status === 'queued';
  const failed = group.status === 'failed';
  return (
    <div className="rounded-md border border-outline-variant/30 bg-surface p-2.5">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-neutral-500">
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-700">
          第 {index} 次
        </span>
        <span>{formatTime(group.createdAt)}</span>
        <span>· {group.size}</span>
        {running && (
          <span className="inline-flex items-center gap-1 text-brand-600">
            <Loader2 className="h-3 w-3 animate-spin" /> 生成中…
          </span>
        )}
        {failed && (
          <span className="inline-flex items-center gap-1 text-rose-600">
            <RotateCcw className="h-3 w-3" /> 失败
            {group.error && (
              <span className="ml-1 max-w-[200px] truncate" title={group.error}>
                · {group.error}
              </span>
            )}
          </span>
        )}
        <button
          type="button"
          onClick={() => onDelete(group.id)}
          disabled={deleting || running}
          title={running ? '生成中不可删除' : '删除该组候选'}
          className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {deleting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Trash2 className="h-3 w-3" />
          )}
          删除
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {(() => {
          const total = Math.max(
            group.count ?? 0,
            group.candidates.length,
            running ? 1 : 0,
          );
          const slots = Array.from({ length: total });
          return slots.map((_, i) => {
            const c = group.candidates[i];
            if (!c) {
              return (
                <div
                  key={`ph-${i}`}
                  className="flex aspect-[2/1] animate-pulse items-center justify-center rounded border border-dashed border-outline-variant/40 bg-surface-variant/40 text-outline"
                >
                  <ImagePlus className="h-4 w-4" />
                </div>
              );
            }
            const active = selectedCandidateId === c.id;
            const ok = !!c.url && !c.error;
            return (
              <div
                key={c.id}
                className={
                  'group relative aspect-[2/1] overflow-hidden rounded border transition ' +
                  (active
                    ? 'border-brand-500 ring-2 ring-brand-200'
                    : ok
                    ? 'border-outline-variant/40 hover:border-brand-300'
                    : 'border-rose-200 bg-rose-50/40')
                }
              >
                {ok ? (
                  <>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => onSelect(c.id)}
                      className="block h-full w-full"
                      title={active ? '已选定' : '点击设为本剧人物设定'}
                    >
                      <img
                        src={c.url ?? undefined}
                        alt="候选"
                        className="h-full w-full object-cover"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (c.url) onView(c.url);
                      }}
                      className="absolute left-0.5 top-0.5 inline-flex h-5 w-5 items-center justify-center rounded bg-black/55 text-white opacity-0 transition group-hover:opacity-100"
                      title="查看大图"
                    >
                      <Maximize2 className="h-2.5 w-2.5" />
                    </button>
                  </>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center text-[10px] text-rose-500">
                    <ImageOff className="h-4 w-4" />
                    {c.error || '失败'}
                  </div>
                )}
                {active && (
                  <div className="absolute right-0.5 top-0.5 flex items-center gap-0.5">
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-brand-500 px-1 py-0.5 text-[9px] text-white shadow">
                      <Check className="h-2 w-2" /> 已选
                    </span>
                    <button
                      type="button"
                      disabled={deselecting}
                      onClick={(e) => { e.stopPropagation(); onDeselect(); }}
                      className="inline-flex items-center justify-center rounded-full bg-neutral-600/80 p-0.5 text-white shadow hover:bg-red-500 transition disabled:opacity-60"
                      title="取消选定"
                    >
                      <X className="h-2 w-2" />
                    </button>
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

function EmptyHint({ icon, msg }: { icon: React.ReactNode; msg: string }) {
  return (
    <div className="flex h-32 flex-col items-center justify-center gap-2 text-center text-xs text-neutral-500">
      {icon}
      <p className="px-4 leading-relaxed">{msg}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// 形象补充说明(跟随 activeLook)
// ────────────────────────────────────────────────────────────

function LookNotesField({
  episodeId,
  characterId,
  collection,
  activeLook,
  lookIdParam,
  characterNotes,
}: {
  episodeId: string;
  characterId: string;
  collection: 'characters' | 'npcs';
  activeLook: CharacterLook | null;
  lookIdParam: string | undefined;
  characterNotes: string;
}) {
  const qc = useQueryClient();
  const sourceNotes = activeLook?.notes ?? characterNotes;
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(sourceNotes);
  const [hint, setHint] = useState('');
  const [aiPreview, setAiPreview] = useState<string | null>(null);

  useEffect(() => {
    setNotes(activeLook?.notes ?? characterNotes);
    setAiPreview(null);
    setEditing(false);
  }, [activeLook?.id, activeLook?.notes, characterNotes]);

  const dirty = notes !== sourceNotes;

  const saveNotes = useMutation({
    mutationFn: () => {
      if (lookIdParam) {
        return api
          .patch(
            `director/episodes/${episodeId}/director/${collection}/${characterId}/looks/${lookIdParam}`,
            { json: { notes: notes || null } },
          )
          .json();
      }
      return api
        .patch(
          `director/episodes/${episodeId}/director/${collection}/${characterId}`,
          { json: { notes: notes || null } },
        )
        .json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('补充说明已保存');
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  const generateMut = useMutation({
    mutationFn: async () => {
      if (lookIdParam) {
        return api
          .post(
            `director/episodes/${episodeId}/director/${collection}/${characterId}/looks/${lookIdParam}/generate-notes`,
            { json: { userHint: hint.trim() || null, lookId: lookIdParam } },
          )
          .json<{ notes: string }>();
      }
      return api
        .post(
          `director/episodes/${episodeId}/director/${collection}/${characterId}/generate-notes`,
          { json: { userHint: hint.trim() || null } },
        )
        .json<{ notes: string }>();
    },
    onSuccess: (res) => {
      setAiPreview(res.notes);
    },
    onError: (e: Error) => toast.error('AI 生成失败', e.message || '请重试'),
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-semibold text-neutral-500">
          补充说明{activeLook && activeLook.id !== '__base__' && ` · ${activeLook.label}`}
        </label>
        {!editing && (
          <button
            type="button"
            onClick={() => { setNotes(sourceNotes); setEditing(true); }}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-neutral-400 transition hover:text-brand-600"
          >
            <Pencil className="h-3 w-3" /> 编辑
          </button>
        )}
      </div>

      {!editing ? (
        <div className="min-h-[3rem] rounded border border-outline-variant/20 bg-surface-variant/20 px-2 py-1.5 text-[11px] leading-relaxed text-neutral-600 whitespace-pre-wrap">
          {sourceNotes || <span className="text-neutral-400">暂无补充说明</span>}
        </div>
      ) : (
        <>
          <textarea
            className="w-full resize-none rounded border border-outline-variant/30 bg-white px-2 py-1 text-[11px] leading-relaxed text-neutral-700 placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none"
            rows={10}
            placeholder="为 AI 生成补充额外的人物说明、形象要求…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div className="space-y-1.5 rounded-md border border-brand-100 bg-brand-50/40 p-2">
              <div className="text-[11px] font-medium text-brand-700">AI 生成造型描述</div>
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !generateMut.isPending && generateMut.mutate()}
                  placeholder="可选：补充要求，如「古典宫装」「战损状态」…"
                  className="min-w-0 flex-1 rounded border border-outline-variant/40 bg-white px-2 py-1 text-[11px] text-neutral-700 placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none"
                />
                <Button
                  size="sm"
                  variant="agent"
                  agentColor="director"
                  onClick={() => generateMut.mutate()}
                  disabled={generateMut.isPending}
                >
                  {generateMut.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Wand2 className="h-3 w-3" />
                  )}
                  {generateMut.isPending ? '生成中…' : 'AI 生成'}
                </Button>
              </div>

              {aiPreview !== null && (
                <div className="space-y-1.5">
                  <div className="text-[10px] text-brand-600">生成结果（确认后点「使用」填入）</div>
                  <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded border border-brand-200 bg-white px-2 py-1.5 text-[11px] leading-relaxed text-neutral-700">
                    {aiPreview}
                  </pre>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="agent"
                      agentColor="director"
                      onClick={() => { setNotes(aiPreview); setAiPreview(null); }}
                    >
                      <Check className="h-3 w-3" /> 使用
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setAiPreview(null)}>
                      <X className="h-3 w-3" /> 放弃
                    </Button>
                  </div>
                </div>
              )}
            </div>

          <div className="flex justify-end gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setNotes(sourceNotes); setEditing(false); setAiPreview(null); }}
              disabled={saveNotes.isPending}
            >
              <X className="h-3 w-3" /> 取消
            </Button>
            <Button
              size="sm"
              variant="agent"
              agentColor="director"
              onClick={() => saveNotes.mutate()}
              disabled={saveNotes.isPending || !dirty}
            >
              {saveNotes.isPending ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <Save className="h-2.5 w-2.5" />
              )}
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// 形象选择器 + 新建表单
// ────────────────────────────────────────────────────────────

function LookSelector({
  looks,
  activeLookId,
  characterName,
  episodes,
  episodeId,
  characterId,
  collection,
  showNewForm,
  onSelect,
  onToggleNew,
  onCreated,
  onView,
}: {
  looks: CharacterLook[];
  activeLookId: string | null;
  characterName: string;
  episodes?: DirectorEpisodePlan[];
  episodeId: string;
  characterId: string;
  collection: 'characters' | 'npcs';
  showNewForm: boolean;
  onSelect: (id: string) => void;
  onToggleNew: () => void;
  onCreated: (id: string) => void;
  onView: (url: string) => void;
}) {
  const qc = useQueryClient();
  const [newEpIdx, setNewEpIdx] = useState<number | ''>('');
  const [newLabel, setNewLabel] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingLookId, setEditingLookId] = useState<string | null>(null);
  const [editEpIdx, setEditEpIdx] = useState<number | ''>('');
  const [editLabel, setEditLabel] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const previewLabel = useMemo(() => {
    const label = newLabel.trim();
    if (label) return label;
    const ep = newEpIdx !== '' ? `第${newEpIdx}集` : '';
    const desc = newDesc.trim();
    if (!ep && !desc) return `${characterName}·新形象`;
    if (ep && desc) return `${ep}·${characterName}·${desc}形象`;
    if (ep) return `${ep}·${characterName}·形象`;
    return `${characterName}·${desc}形象`;
  }, [newLabel, newEpIdx, newDesc, characterName]);

  const createMut = useMutation({
    mutationFn: async () =>
      api
        .post(
          `director/episodes/${episodeId}/director/${collection}/${characterId}/looks`,
          {
            json: {
              label: newLabel.trim() || previewLabel,
              episodeIndex: newEpIdx !== '' ? newEpIdx : null,
              description: newDesc.trim() || null,
            },
          },
        )
        .json<{ characterId: string; look: CharacterLook }>(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setNewEpIdx('');
      setNewLabel('');
      setNewDesc('');
      onCreated(res.look.id);
      toast.success('新形象已创建', res.look.label);
    },
    onError: (e: Error) => toast.error('创建失败', e.message || '请重试'),
  });

  const updateMut = useMutation({
    mutationFn: async ({ lookId, label, epIdx, desc }: { lookId: string; label: string; epIdx: number | ''; desc: string }) =>
      api
        .patch(
          `director/episodes/${episodeId}/director/${collection}/${characterId}/looks/${lookId}`,
          {
            json: {
              label: label.trim() || null,
              episodeIndex: epIdx !== '' ? epIdx : null,
              description: desc.trim() || null,
            },
          },
        )
        .json<{ characterId: string; look: CharacterLook }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setEditingLookId(null);
      toast.success('形象信息已更新');
    },
    onError: (e: Error) => toast.error('更新失败', e.message || '请重试'),
  });

  const sortedEpisodes = useMemo(
    () => [...(episodes ?? [])].sort((a, b) => a.episodeIndex - b.episodeIndex),
    [episodes],
  );

  return (
    <section className="space-y-2">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">形象</h3>
      </header>
      <div className="flex items-start gap-2.5 overflow-x-auto pb-1">
        {looks.map((lk) => {
          const active = lk.id === activeLookId;
          const isBase = lk.id === '__base__';
          const isEditing = editingLookId === lk.id;
          return (
            <div key={lk.id} className="flex shrink-0 flex-col gap-1">
              <button
                type="button"
                onClick={() => onSelect(lk.id)}
                title={lk.label}
                className={
                  'group flex shrink-0 flex-col items-center gap-1.5 rounded-lg border px-2.5 py-2 text-center transition ' +
                  (active
                    ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-200'
                    : 'border-outline-variant/40 bg-surface hover:border-violet-300')
                }
              >
                <div className="relative h-28 w-60 min-h-[7rem] shrink-0 overflow-hidden rounded-md bg-neutral-100">
                  {lk.profileImageUrl ? (
                    <>
                      <img
                        src={lk.profileImageUrl}
                        alt={lk.label}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onView(lk.profileImageUrl!); }}
                        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition group-hover:opacity-100"
                        title="查看大图"
                      >
                        <Maximize2 className="h-5 w-5 text-white" />
                      </button>
                    </>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-neutral-300">
                      <ImagePlus className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <span className="w-60 truncate text-xs leading-tight text-neutral-700">
                  {lk.label}
                </span>
              </button>
              {!isBase ? (
                <button
                  type="button"
                  onClick={() => {
                    if (isEditing) {
                      setEditingLookId(null);
                    } else {
                      setEditingLookId(lk.id);
                      setEditEpIdx(lk.episodeIndex ?? '');
                      setEditLabel(lk.label ?? '');
                      setEditDesc(lk.description ?? '');
                    }
                  }}
                  className="inline-flex items-center justify-center gap-1 rounded-md py-0.5 text-[11px] text-neutral-400 transition hover:text-violet-600"
                >
                  {isEditing ? <X className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                  {isEditing ? '取消' : '编辑信息'}
                </button>
              ) : (
                <span aria-hidden className="py-0.5 text-[11px] leading-none">&nbsp;</span>
              )}
              {isEditing && (
                <div className="w-60 space-y-1.5 rounded-lg border border-violet-200 bg-violet-50/40 p-2">
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-neutral-600">集数</label>
                    <select
                      value={editEpIdx}
                      onChange={(e) => setEditEpIdx(e.target.value === '' ? '' : Number(e.target.value))}
                      className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1 text-xs text-neutral-700 focus:border-violet-400 focus:outline-none"
                    >
                      <option value="">— 不限集</option>
                      {sortedEpisodes.map((ep) => (
                        <option key={ep.episodeIndex} value={ep.episodeIndex}>
                          第{ep.episodeIndex}集
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-neutral-600">形象名称</label>
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      maxLength={40}
                      placeholder="如：矿工装 / 礼服 / 战损"
                      className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1 text-xs text-neutral-700 placeholder:text-neutral-400 focus:border-violet-400 focus:outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-neutral-600">形象描述</label>
                    <input
                      type="text"
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      maxLength={120}
                      placeholder="补充这个形象的服装、状态、场景用途"
                      className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1 text-xs text-neutral-700 placeholder:text-neutral-400 focus:border-violet-400 focus:outline-none"
                    />
                  </div>
                  <Button
                    variant="agent"
                    agentColor="director"
                    size="sm"
                    className="w-full"
                    disabled={updateMut.isPending || !editLabel.trim()}
                    onClick={() => updateMut.mutate({ lookId: lk.id, label: editLabel, epIdx: editEpIdx, desc: editDesc })}
                  >
                    {updateMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    保存
                  </Button>
                  <button
                    type="button"
                    className="w-full rounded-md py-1 text-[11px] text-danger transition hover:bg-danger-soft/30"
                    onClick={() => {
                      if (window.confirm('确定删除此形象？删除后不可恢复。')) {
                        api.delete(`director/episodes/${episodeId}/director/${collection}/${characterId}/looks/${lk.id}`).json().then(() => {
                          qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
                          onSelect('__base__');
                          setEditingLookId(null);
                          toast.success('形象已删除');
                        }).catch(() => toast.error('删除失败'));
                      }
                    }}
                  >
                    删除此形象
                  </button>
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={onToggleNew}
          className={
            'flex shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-3 text-xs transition ' +
            (showNewForm
              ? 'border-violet-400 text-violet-600'
              : 'border-outline-variant/40 text-neutral-500 hover:border-violet-300 hover:text-violet-600')
          }
        >
          {showNewForm ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {showNewForm ? '取消' : '新增形象'}
        </button>
      </div>

      {showNewForm && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-violet-200 bg-violet-50/40 p-3">
          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-neutral-600">集数</label>
            <select
              value={newEpIdx}
              onChange={(e) =>
                setNewEpIdx(e.target.value === '' ? '' : Number(e.target.value))
              }
              className="rounded border border-outline-variant/40 bg-white px-2 py-1 text-xs text-neutral-700 focus:border-violet-400 focus:outline-none"
            >
              <option value="">— 不限集</option>
              {sortedEpisodes.map((ep) => (
                <option key={ep.episodeIndex} value={ep.episodeIndex}>
                  第{ep.episodeIndex}集
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-neutral-600">角色</label>
            <span className="inline-block rounded border border-outline-variant/30 bg-neutral-50 px-2 py-1 text-xs text-neutral-500">
              {characterName}
            </span>
          </div>
          <div className="min-w-[120px] flex-1 space-y-1">
            <label className="block text-[11px] font-medium text-neutral-600">形象名称</label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              maxLength={40}
              placeholder="如:矿工装 / 礼服 / 战损"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1 text-xs text-neutral-700 placeholder:text-neutral-400 focus:border-violet-400 focus:outline-none"
            />
          </div>
          <div className="min-w-[180px] flex-[1.5] space-y-1">
            <label className="block text-[11px] font-medium text-neutral-600">形象描述</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              maxLength={120}
              placeholder="补充这个形象的服装、状态、场景用途"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1 text-xs text-neutral-700 placeholder:text-neutral-400 focus:border-violet-400 focus:outline-none"
            />
          </div>
          <div className="flex items-end gap-2">
            <span className="self-center text-[11px] text-neutral-400">
              {previewLabel}
            </span>
            <Button
              variant="agent"
              agentColor="director"
              size="sm"
              disabled={createMut.isPending || !previewLabel.trim()}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              创建
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

// ────────────────────────────────────────────────────────────
// 「存为数字资产」一键确认弹窗 · 当前 profile → Brain 资产
// ────────────────────────────────────────────────────────────

function SaveAsAssetConfirmDialog({
  open,
  onClose,
  character,
  defaultName,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  character: ProfileSubject;
  defaultName: string;
  onSaved: () => void;
}) {
  const [name, setName] = useState(defaultName);

  useEffect(() => {
    if (open) setName(defaultName);
  }, [open, defaultName]);

  const profileUrl = character.profileImageUrl ?? null;

  const attributes = useMemo<Record<string, string>>(() => {
    // 主角 / NPC 字段差异:对 NPC 来说 personality / wardrobe / cameraNotes 为 undefined
    const c = character as DirectorCharacterCard;
    const a: Record<string, string> = {};
    const pick = (k: string, v?: string | null) => {
      if (v && v.trim()) a[k] = v.trim();
    };
    pick('role', c.role);
    pick('gender', c.gender);
    pick('appearance', c.appearance);
    pick('personality', c.personality);
    pick('wardrobe', c.wardrobe);
    pick('cameraNotes', c.cameraNotes);
    return a;
  }, [character]);

  const saveMut = useMutation({
    mutationFn: async () =>
      api
        .post('brain/assets/from-locked-url', {
          json: {
            kind: 'character',
            name: name.trim() || defaultName,
            lockedImageUrl: profileUrl,
            attributes,
            referenceImages: [],
          },
        })
        .json<BrainAsset>(),
    onSuccess: () => onSaved(),
    onError: (e: Error) => toast.error('存为资产失败', e.message || '请重试'),
  });

  if (!open) return null;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="存为数字资产"
      description="将当前人物设定 + 剧情字段一键快照成 Brain 资产 · 可在其他剧目复用"
      className="max-w-md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saveMut.isPending}>
            取消
          </Button>
          <Button
            variant="agent"
            agentColor="director"
            size="sm"
            disabled={saveMut.isPending || !profileUrl || !name.trim()}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> 保存中…
              </>
            ) : (
              <>
                <Save className="mr-1 h-3.5 w-3.5" /> 确认存为资产
              </>
            )}
          </Button>
        </div>
      }
    >
      {!profileUrl ? (
        <p className="px-1 py-2 text-xs text-rose-600">
          当前人物设定为空 · 请先选定或生成一张候选
        </p>
      ) : (
        <div className="space-y-3 px-1 pb-1">
          <div className="flex gap-3">
            <img
              src={profileUrl}
              alt="当前人物设定"
              className="h-28 w-28 shrink-0 rounded-md object-cover ring-1 ring-outline-variant/40"
            />
            <div className="flex-1 space-y-2">
              <label className="block text-[11px] font-medium text-neutral-600">
                资产名称
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
              />
              <p className="text-[10px] text-neutral-400">
                自动以 Brain 资产命名规则给出建议值,可自由修改
              </p>
            </div>
          </div>
          {Object.keys(attributes).length > 0 && (
            <div>
              <div className="mb-1 text-[11px] font-medium text-neutral-600">
                将随资产一并存入的剧情字段
              </div>
              <dl className="grid gap-1 rounded-md bg-surface-variant/40 px-2 py-1.5 sm:grid-cols-2">
                {Object.entries(attributes).map(([k, v]) => (
                  <div key={k} className="text-[11px] leading-relaxed">
                    <dt className="inline text-neutral-500">{ATTR_LABELS[k] ?? k}:</dt>{' '}
                    <dd className="inline text-neutral-700">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

const ATTR_LABELS: Record<string, string> = {
  role: '角色定位',
  gender: '性别',
  appearance: '外貌',
  personality: '性格',
  wardrobe: '服饰',
  cameraNotes: '镜头建议',
};
